import { useState, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

function useDebouncedCallback(callback, delay) {
  const timer = useRef(null);
  return (...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => callback(...args), delay);
  };
}

const COORD_REGEX =
  /^\s*(-?\d{1,3}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;

function parseCoordinates(text) {
  const match = text.match(COORD_REGEX);
  if (!match) return null;

  const lat = parseFloat(match[1]);
  const lng = parseFloat(match[2]);

  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat < -90 || lat > 90) return null;
  if (lng < -180 || lng > 180) return null;

  return { lat, lng };
}

function CoordinateJump({ onFly }) {
  const [latLng, setLatLng] = useState("");
  const [error, setError] = useState("");

  const handleGo = () => {
    const coords = parseCoordinates(latLng);
    if (!coords) {
      setError("Enter valid coordinates, e.g. 28.6139, 77.2090");
      return;
    }
    setError("");
    onFly(coords);
  };

  const handleChange = (e) => {
    setLatLng(e.target.value);
    if (error) setError(""); // clear stale error as soon as they start retyping
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type="text"
          value={latLng}
          onChange={handleChange}
          onKeyDown={(e) => e.key === "Enter" && handleGo()}
          placeholder="Lat, Lng (e.g. 28.6139, 77.2090)"
          style={{
            flex: 1,
            padding: "8px 12px",
            borderRadius: 6,
            border: "1px solid #ccc",
            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          }}
        />
        <button
          type="button"
          onClick={handleGo}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: "1px solid #ccc",
            background: "#fff",
            cursor: "pointer",
            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          }}
        >
          Go
        </button>
      </div>
      {error && (
        <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default function AddressSearch({ onLocationSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const map = useMap();
  const markerRef = useRef(null);

  const dropMarker = (lat, lng, label) => {
    if (markerRef.current) {
      map.removeLayer(markerRef.current);
    }
    const marker = L.marker([lat, lng]);
    if (label) marker.bindPopup(label);
    marker.addTo(map);
    markerRef.current = marker;
  };

  const search = async (text) => {
    if (!text || text.trim().length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.locationiq.com/v1/autocomplete?key=${process.env.REACT_APP_LOCATIONIQ_KEY}&q=${encodeURIComponent(text)}&limit=5&countrycodes=in`,
      );
      const data = await res.json();
      setResults(data);
    } catch (err) {
      console.error("Geocoding failed:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useDebouncedCallback(search, 400);

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    debouncedSearch(value);
  };

  const handleSelect = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setQuery(result.display_name);
    setResults([]);
    map.flyTo([lat, lng], 16);
    dropMarker(lat, lng, result.display_name);
    onLocationSelect?.({ lat, lng, address: result.display_name });
  };

  const handleFlyToCoords = ({ lat, lng }) => {
    map.flyTo([lat, lng], 16);
    dropMarker(lat, lng, `${lat}, ${lng}`);
    onLocationSelect?.({ lat, lng, address: `${lat}, ${lng}` });
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 10,
        left: 50,
        zIndex: 1000,
        width: 320,
      }}
    >
      <input
        type="text"
        value={query}
        onChange={handleChange}
        placeholder="Search an address..."
        style={{
          width: "100%",
          padding: "8px 12px",
          borderRadius: 6,
          border: "1px solid #ccc",
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
        }}
      />
      {loading && (
        <div style={{ background: "#fff", padding: 6 }}>Searching...</div>
      )}
      {results.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 6,
            marginTop: 4,
            maxHeight: 200,
            overflowY: "auto",
          }}
        >
          {results.map((r) => (
            <li
              key={r.place_id}
              onClick={() => handleSelect(r)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                borderBottom: "1px solid #eee",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f5f5f5")
              }
              onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
            >
              {r.display_name}
            </li>
          ))}
        </ul>
      )}

      <CoordinateJump onFly={handleFlyToCoords} />
    </div>
  );
}
